/**
 * Accessible listbox + trigger, driven by existing `<input type="radio">` (no shadow DOM, no deps).
 *
 * API: `WD_CustomSelect.mount(root, opts)` — see typedef below. Selection uses `radio.click()` so host
 * `change` listeners keep working. Default styles: `wd-styles-custom-select.css` (override in theme).
 *
 * @typedef {Object} WD_CustomSelectMountOptions
 * @property {Iterable<HTMLInputElement>} radios - One group (same `name`, same picker row).
 * @property {HTMLElement} anchor - Sibling reference: UI is inserted `insertBefore(anchor)` on `anchor.parentElement`.
 * @property {string} [labelledBy] - Element `id` for `aria-labelledby` on trigger + listbox (e.g. row title).
 * @property {string} [className] - Extra class on the root `.wd-custom-select` node.
 */

(function () {
  'use strict';

  const ROOT_CLASS = 'wd-custom-select';

  // --- DOM helpers (clone label → option body, strip ids/inputs so clones are safe) ---

  /** @param {HTMLInputElement} radio */
  function labelEl(radio) {
    return radio.closest('label');
  }

  /** Price line on `<label after="…">` (maintained by `WD_VariantOption.refreshDynamicPriceAfter` for `.wd-dynamic-price`). */
  /** @param {HTMLInputElement} radio */
  function priceAfterAttr(radio) {
    const v = labelEl(radio)?.getAttribute('after');
    return v != null && v !== '' ? v : null;
  }

  /**
   * Closed trigger: “whole” price line — `handle` uses diff vs `base_price` (same as old Liquid); other `.wd-dynamic-price`
   * rows show `(תוספת …)` from option `data-price` (variant amount), not row-relative (label `after` is vs selected).
   * @param {HTMLInputElement} radio
   */
  function valueSlotAfterAttr(radio) {
    if (!radio.classList.contains('wd-dynamic-price') || typeof WD_VariantOption === 'undefined')
      return priceAfterAttr(radio);
    const optMajor = Number(radio.dataset?.price ?? 0);
    const base = Number(window.current_product?.base_price ?? 0);
    const param = radio.closest('wd-product-variant-picker')?.getAttribute('param');
    if (param === 'handle')
      return WD_VariantOption.formatDynamicAfterDiff(optMajor - base);
    if (optMajor > 0)
      return `(תוספת ${WD_VariantOption.formatMoneyMajor(optMajor)})`;
    return null;
  }

  /** @param {HTMLInputElement} radio */
  function cloneLabelBody(radio) {
    const lab = labelEl(radio);
    const shell = document.createElement('div');
    shell.className = `${ROOT_CLASS}__option-body`;
    if (!lab) return shell;
    const clone = /** @type {HTMLElement} */ (lab.cloneNode(true));
    clone.querySelectorAll('input').forEach((n) => n.remove());
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    while (clone.firstChild) shell.appendChild(clone.firstChild);
    return shell;
  }

  /** Next/prev index in `list` skipping disabled radios (wraps). @param {HTMLInputElement[]} list @param {number} from @param {number} delta @returns {number} */
  function nextEnabledIndex(list, from, delta) {
    const n = list.length;
    if (n === 0) return -1;
    let i = from;
    for (let s = 0; s < n; s++) {
      i = (i + delta + n) % n;
      if (!list[i].disabled) return i;
    }
    return -1;
  }

  // --- Mount: build DOM, wire a11y + keyboard, return public handles ---

  /**
   * @param {HTMLElement} root - Event delegate for `change` (e.g. `<wd-product-variant-picker>`).
   * @param {WD_CustomSelectMountOptions} opts
   * @returns {{ destroy: () => void, syncFromRadios: () => void, open: () => void, close: () => void }}
   */
  function mount(root, opts) {
    const radios = Array.from(opts.radios).filter((r) => r instanceof HTMLInputElement && r.type === 'radio');
    const anchor = opts.anchor;
    if (!anchor?.parentElement || radios.length === 0) {
      return { destroy() { }, syncFromRadios() { }, open() { }, close() { } };
    }

    const uid = (globalThis.crypto?.randomUUID?.() ?? `wd-cs-${Date.now()}-${Math.random().toString(16).slice(2)}`).replace(/[^a-zA-Z0-9-]/g, '');
    const idTrigger = `${uid}-trigger`;
    const idList = `${uid}-listbox`;

    const wrap = document.createElement('div');
    wrap.className = [ROOT_CLASS, opts.className].filter(Boolean).join(' ');
    wrap.dataset.wdCustomSelect = '';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `${ROOT_CLASS}__trigger`;
    btn.id = idTrigger;
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', idList);

    const valueSlot = document.createElement('span');
    valueSlot.className = `${ROOT_CLASS}__value`;
    valueSlot.id = `${uid}-value`;
    btn.appendChild(valueSlot);
    if (opts.labelledBy) btn.setAttribute('aria-labelledby', `${opts.labelledBy} ${valueSlot.id}`);

    const pop = document.createElement('div');
    pop.className = `${ROOT_CLASS}__popover`;
    pop.hidden = true;

    const list = document.createElement('ul');
    list.className = `${ROOT_CLASS}__list`;
    list.id = idList;
    list.setAttribute('role', 'listbox');
    list.tabIndex = -1;
    if (opts.labelledBy) list.setAttribute('aria-labelledby', opts.labelledBy);

    /** @type {{ radio: HTMLInputElement, li: HTMLLIElement }[]} */
    const rows = [];
    for (const radio of radios) {
      const li = document.createElement('li');
      li.setAttribute('style', radio.closest('label')?.getAttribute('style') ?? '');
      li.className = `${ROOT_CLASS}__option`;
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', radio.checked ? 'true' : 'false');
      if (radio.disabled) li.setAttribute('aria-disabled', 'true');
      li.tabIndex = -1;
      // Purely visual “radio” (no input); selection follows real radios via `aria-selected` in `syncFromRadios`.
      const radioVisual = document.createElement('span');
      radioVisual.className = `${ROOT_CLASS}__radio-visual`;
      radioVisual.setAttribute('aria-hidden', 'true');
      li.appendChild(radioVisual);
      li.appendChild(cloneLabelBody(radio));
      const lab = labelEl(radio);
      if (lab?.id) li.setAttribute('data-wd-info-id', lab.id);
      const afterStr = priceAfterAttr(radio);
      if (afterStr) li.setAttribute('after', afterStr);
      li.addEventListener('click', (e) => {
        e.preventDefault();
        if (radio.disabled) return;
        radio.click();
        close();
        btn.focus();
      });
      list.appendChild(li);
      rows.push({ radio, li });
    }

    pop.appendChild(list);
    wrap.appendChild(btn);
    wrap.appendChild(pop);
    anchor.parentElement.insertBefore(wrap, anchor);

    let open = false;
    /** @type {number} Index into `radios` / `rows` for keyboard highlight */
    let activeIndex = Math.max(0, radios.findIndex((r) => r.checked));

    function syncFromRadios() {
      const checked = radios.find((r) => r.checked);
      const displayRadio = checked ?? radios[0];
      activeIndex = displayRadio ? radios.indexOf(displayRadio) : 0;
      valueSlot.replaceChildren(cloneLabelBody(displayRadio));
      const valueAfter = valueSlotAfterAttr(displayRadio);
      if (valueAfter) valueSlot.setAttribute('after', valueAfter);
      else valueSlot.removeAttribute('after');
      for (const { radio, li } of rows) {
        li.setAttribute('aria-selected', radio.checked ? 'true' : 'false');
        const rowAfter = priceAfterAttr(radio);
        if (rowAfter) li.setAttribute('after', rowAfter);
        else li.removeAttribute('after');
      }
    }

    function setExpanded(v) {
      open = v;
      btn.setAttribute('aria-expanded', v ? 'true' : 'false');
      pop.hidden = !v;
    }

    /** Roving tabindex: one `li` gets `tabindex=0` while list is open. */
    /** @param {HTMLElement} el */
    function focusOptionEl(el) {
      rows.forEach(({ li }) => {
        li.tabIndex = -1;
      });
      el.tabIndex = 0;
      el.focus();
    }

    function focusActiveOption() {
      const i = activeIndex >= 0 ? activeIndex : 0;
      const row = rows[i];
      if (row && !row.radio.disabled) {
        focusOptionEl(row.li);
        return;
      }
      const j = nextEnabledIndex(radios, Math.max(0, i), 1);
      if (j >= 0) {
        activeIndex = j;
        focusOptionEl(rows[j].li);
      }
    }

    function openList() {
      if (open) return;
      setExpanded(true);
      const start = radios.findIndex((r) => r.checked);
      activeIndex = start >= 0 ? start : nextEnabledIndex(radios, 0, 1);
      requestAnimationFrame(() => {
        focusActiveOption();
      });
    }

    function close() {
      if (!open) return;
      setExpanded(false);
      rows.forEach(({ li }) => {
        li.tabIndex = -1;
      });
    }

    /** Listbox: arrows, Home/End, Enter/Space to commit, Escape to close, Tab closes (default focus move). */
    /** @param {KeyboardEvent} e */
    function onListKeydown(e) {
      if (!open) return;
      const { key } = e;
      if (key === 'Escape') {
        e.preventDefault();
        close();
        btn.focus();
        return;
      }
      if (key === 'Tab') {
        close();
        return;
      }
      if (key === 'ArrowDown' || key === 'ArrowUp') {
        e.preventDefault();
        const delta = key === 'ArrowDown' ? 1 : -1;
        const j = nextEnabledIndex(radios, activeIndex, delta);
        if (j >= 0) {
          activeIndex = j;
          focusOptionEl(rows[j].li);
          rows[j].li.scrollIntoView({ block: 'nearest' });
        }
        return;
      }
      if (key === 'Home') {
        e.preventDefault();
        const j = nextEnabledIndex(radios, 0, 1);
        if (j >= 0) {
          activeIndex = j;
          focusOptionEl(rows[j].li);
        }
        return;
      }
      if (key === 'End') {
        e.preventDefault();
        const j = nextEnabledIndex(radios, radios.length - 1, -1);
        if (j >= 0) {
          activeIndex = j;
          focusOptionEl(rows[j].li);
        }
        return;
      }
      if (key === 'Enter' || key === ' ') {
        e.preventDefault();
        const r = radios[activeIndex];
        if (r && !r.disabled) {
          r.click();
          close();
          btn.focus();
        }
      }
    }

    /** Closed trigger: open on Enter/Space/ArrowDown; ArrowUp opens and focuses last enabled option. */
    /** @param {KeyboardEvent} e */
    function onBtnKeydown(e) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openList();
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        openList();
        const j = nextEnabledIndex(radios, radios.length - 1, -1);
        if (j >= 0) {
          activeIndex = j;
          requestAnimationFrame(() => focusOptionEl(rows[j].li));
        }
      }
    }

    function onDocPointerDown(e) {
      if (!open) return;
      const t = /** @type {Node} */ (e.target);
      if (!wrap.contains(t)) close();
    }

    function onRootChange(e) {
      const t = e.target;
      if (t instanceof HTMLInputElement && t.type === 'radio' && radios.includes(t)) syncFromRadios();
    }

    btn.addEventListener('click', () => {
      if (open) {
        close();
        return;
      }
      openList();
    });
    btn.addEventListener('keydown', onBtnKeydown);
    list.addEventListener('keydown', onListKeydown);
    document.addEventListener('pointerdown', onDocPointerDown, true);
    root.addEventListener('change', onRootChange);

    syncFromRadios();

    return {
      syncFromRadios,
      open: openList,
      close,
      destroy() {
        document.removeEventListener('pointerdown', onDocPointerDown, true);
        root.removeEventListener('change', onRootChange);
        btn.removeEventListener('keydown', onBtnKeydown);
        list.removeEventListener('keydown', onListKeydown);
        wrap.remove();
      },
    };
  }

  globalThis.WD_CustomSelect = { mount };
})();
